<template>
  <div>
    <div class="d-flex flex-column-fluid">
      <div class="container-fluid">
        <div class="row">
          <div class="col-12">
            <div class="card card-custom gutter-b bg-transparent shadow-none border-0">
              <div class="card-header align-items-center border-bottom-dark px-0">
                <div class="card-title mb-0">
                  <h3 class="card-label mb-0 font-weight-bold text-body">Appointments</h3>
                  <div class="small text-muted">Requests from the "Book an In-Store Appointment" form on the website.</div>
                </div>
                <div>
                  <button type="button" class="btn btn-primary btn-sm" @click="exportCsv()">Export to Excel</button>
                </div>
              </div>
            </div>

            <!-- Stats -->
            <div class="row">
              <div class="col-6 col-lg" v-for="s in statCards" :key="s.label">
                <div class="card card-custom gutter-b bg-white border-0" style="cursor:pointer;" @click="s.view && setView(s.view)">
                  <div class="card-body py-4">
                    <div class="text-muted small">{{ s.label }}</div>
                    <div class="font-weight-bold" style="font-size:24px;">{{ s.value }}</div>
                  </div>
                </div>
              </div>
            </div>

            <div class="card card-custom gutter-b bg-white border-0">
              <div class="card-body">
                <div class="dataTables_wrapper no-footer">
                  <div class="d-flex flex-wrap align-items-center justify-content-between mb-3" style="gap:12px;">
                    <div class="btn-group flex-wrap">
                      <button v-for="f in filters" :key="f.value" type="button"
                        class="btn btn-sm" :class="view === f.value ? 'btn-primary' : 'btn-light'"
                        @click="setView(f.value)">{{ f.label }}</button>
                    </div>
                    <div class="dataTables_filter">
                      <label>Search:
                        <input type="text" v-model="search" @keyup="debouncedFetch()" placeholder="Name, email or phone" />
                      </label>
                    </div>
                  </div>

                  <div style="overflow-x:auto;">
                    <table class="display dataTable no-footer" style="width:100%">
                      <thead class="text-body">
                        <tr>
                          <th>Appointment</th>
                          <th>Customer</th>
                          <th>Shopping for</th>
                          <th>Status</th>
                          <th style="min-width:200px;">Note</th>
                          <th>Requested</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-if="rows.length === 0">
                          <td colspan="6" class="text-center">{{ loaded ? 'No appointments here.' : 'Loading...' }}</td>
                        </tr>
                        <tr v-for="a in rows" :key="a.id">
                          <td class="text-nowrap">
                            <strong :class="{ 'text-muted': a.is_past }">{{ prettyDate(a.appointment_date) }}</strong>
                            <div class="small text-muted">{{ a.appointment_window }}<span v-if="a.is_past"> · past</span></div>
                          </td>
                          <td>
                            <strong>{{ a.name }}</strong>
                            <div class="small"><a :href="'mailto:' + a.email">{{ a.email }}</a></div>
                            <div class="small">
                              <a :href="'tel:' + a.mobile">{{ prettyPhone(a.mobile) }}</a>
                              <span class="badge ml-1" :class="a.sms_consent ? 'badge-success' : 'badge-light'"
                                :title="a.sms_consent ? 'Agreed to calls/texts' : 'Did not tick the calls/texts box'">
                                {{ a.sms_consent ? 'texts OK' : 'no texts' }}
                              </span>
                            </div>
                          </td>
                          <td style="max-width:260px; white-space:pre-line;">{{ a.details || '—' }}</td>
                          <td>
                            <select class="form-control form-control-sm" :class="'appt-status-' + a.status" v-model="a.status" @change="save(a, { status: a.status })">
                              <option v-for="s in statuses" :key="s.value" :value="s.value">{{ s.label }}</option>
                            </select>
                          </td>
                          <td>
                            <textarea class="form-control form-control-sm" rows="2" v-model="a.admin_note"
                              placeholder="Private note" @blur="saveNote(a)"></textarea>
                          </td>
                          <td class="text-nowrap small">{{ (a.created_at || '').substring(0, 16) }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div class="dataTables_paginate paging_simple_numbers">
                    <ul class="pagination">
                      <li class="paginate_button page-item" :class="{ disabled: !meta.prev_page_url }">
                        <a class="page-link" href="#" @click.prevent="fetchRows(meta.prev_page_url)">Previous</a>
                      </li>
                      <li class="paginate_button page-item active">
                        <a class="page-link" href="#">{{ meta.current_page }} / {{ meta.last_page }}</a>
                      </li>
                      <li class="paginate_button page-item" :class="{ disabled: !meta.next_page_url }">
                        <a class="page-link" href="#" @click.prevent="fetchRows(meta.next_page_url)">Next</a>
                      </li>
                    </ul>
                    <div class="mt-2 text-muted">Total: {{ meta.total }} appointments</div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      rows: [],
      stats: {},
      meta: {},
      view: 'upcoming',
      search: '',
      token: {},
      timer: null,
      loaded: false,
      savedNotes: {},
      filters: [
        { value: 'upcoming', label: 'Upcoming' },
        { value: 'today', label: 'Today' },
        { value: 'new', label: 'To confirm' },
        { value: 'confirmed', label: 'Confirmed' },
        { value: 'done', label: 'Done' },
        { value: 'cancelled', label: 'Cancelled' },
        { value: 'all', label: 'All' },
        { value: 'spam', label: 'Spam' },
      ],
      statuses: [
        { value: 'new', label: 'New - to confirm' },
        { value: 'confirmed', label: 'Confirmed' },
        { value: 'done', label: 'Done (came in)' },
        { value: 'cancelled', label: 'Cancelled / no-show' },
        { value: 'spam', label: 'Spam' },
      ],
    };
  },
  computed: {
    statCards() {
      const s = this.stats;
      return [
        { label: 'Today', value: s.today || 0, view: 'today' },
        { label: 'Upcoming', value: s.upcoming || 0, view: 'upcoming' },
        { label: 'Waiting to be confirmed', value: s.to_confirm || 0, view: 'new' },
        { label: 'Requested last 7 days', value: s.last_7_days || 0, view: 'all' },
        { label: 'All time', value: s.total || 0, view: 'all' },
      ];
    },
  },
  methods: {
    fetchRows(pageUrl) {
      let url = pageUrl || '/api/admin/appointments';
      url += (url.includes('?') ? '&' : '?') + 'limit=25&view=' + this.view;
      if (this.search !== '') url += '&search=' + encodeURIComponent(this.search);
      axios.get(url, this.token).then((res) => {
        this.rows = res.data.data;
        this.stats = res.data.stats;
        this.meta = res.data.meta;
        this.savedNotes = {};
        this.rows.forEach((a) => { this.savedNotes[a.id] = a.admin_note || ''; });
      }).finally(() => {
        this.loaded = true;
        this.$parent.loading = false;
      });
    },
    setView(view) {
      this.view = view;
      this.fetchRows();
    },
    debouncedFetch() {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.fetchRows(), 300);
    },
    save(a, changes) {
      return axios.post('/api/admin/appointments/' + a.id, changes, this.token)
        .then((res) => { this.$toaster.success(res.data.message); })
        .catch(() => { this.$toaster.error('Could not save. Please try again.'); });
    },
    saveNote(a) {
      const note = a.admin_note || '';
      if (note === this.savedNotes[a.id]) return;
      this.save(a, { admin_note: note }).then(() => { this.savedNotes[a.id] = note; });
    },
    exportCsv() {
      // the API needs the bearer token, so a plain link would be refused
      axios.get('/api/admin/appointments/export', Object.assign({ responseType: 'blob' }, this.token))
        .then((res) => {
          const link = document.createElement('a');
          link.href = URL.createObjectURL(res.data);
          link.download = 'appointments.csv';
          link.click();
          URL.revokeObjectURL(link.href);
        });
    },
    prettyDate(value) {
      if (!value) return '—';
      const p = String(value).substring(0, 10).split('-');
      const d = new Date(+p[0], +p[1] - 1, +p[2]);
      return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
    },
    prettyPhone(value) {
      const d = String(value || '').replace(/\D/g, '');
      const ten = d.length === 11 && d[0] === '1' ? d.substring(1) : d;
      return ten.length === 10 ? '(' + ten.substring(0, 3) + ') ' + ten.substring(3, 6) + '-' + ten.substring(6) : value;
    },
  },
  mounted() {
    const token = localStorage.getItem('token');
    this.token = { headers: { Authorization: `Bearer ${token}` } };
    this.fetchRows();
  },
};
</script>

<style scoped>
/* the theme's height: calc(100% - 30px) stretches every card to the column height when they share one column */
.card-custom.gutter-b { height: auto; }
select[class*="appt-status-"] { min-width: 165px; }
.appt-status-new { border-color: #f0ad4e; background: #fff8ec; }
.appt-status-confirmed { border-color: #3699ff; background: #eef6ff; }
.appt-status-done { border-color: #1bc5bd; background: #ebfaf9; }
.appt-status-cancelled, .appt-status-spam { color: #888; }
</style>
